// Updated: Added park confirmation info banner for tours requiring park approval
import React, { useState, useEffect } from 'react';
import { CalendarRange, ShieldAlert, Users, FileText } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Tour } from '../../types/tours';
import { formatDate, formatTime } from '../../utils/dateUtils';

interface TourDetailBookingProps {
  tour: Tour;
}

const TourDetailBooking: React.FC<TourDetailBookingProps> = ({ tour }) => {
  const [availableDates, setAvailableDates] = useState<Date[]>([]);

  useEffect(() => {
    // Get next 3 available dates
    const dates = getNextAvailableDates(tour.departure, 3);
    setAvailableDates(dates);
  }, [tour.departure]);

  const getNextAvailableDates = (departureTime: string, count: number): Date[] => {
    const dates: Date[] = [];
    const [hours, minutes] = departureTime.split(':').map(Number);
    const today = new Date();
    let currentDate = new Date();
    
    // Set the departure time
    currentDate.setHours(hours, minutes, 0, 0);

    // If it's past the departure time for today, start from tomorrow
    if (today > currentDate) {
      currentDate.setDate(currentDate.getDate() + 1);
    }

    // Get the next available dates
    while (dates.length < count) {
      dates.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return dates;
  };

  return (
    <div>
      {/* Park Confirmation Notice */}
      {tour.requires_park_confirmation && (
        <div className="mb-8 bg-amber-50 border border-amber-200 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-amber-100 rounded-lg shrink-0">
              <ShieldAlert className="w-6 h-6 text-amber-700" />
            </div>
            <div className="space-y-3">
              <h3 className="font-semibold text-amber-900 text-lg">National Park Reservation Required</h3>
              <div className="space-y-2 text-sm text-amber-800">
                {tour.daily_person_limit && (
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-amber-600 shrink-0" />
                    <span>The park has a <strong>daily limit of {tour.daily_person_limit} visitors</strong>. Availability is subject to park capacity.</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-amber-600 shrink-0" />
                  <span>We need the <strong>full name and passport number</strong> of each participant to reserve your spot with the park.</span>
                </div>
                <div className="flex items-center gap-2">
                  <CalendarRange className="w-4 h-4 text-amber-600 shrink-0" />
                  <span>After booking, we submit your request to the park and <strong>confirm availability within 24 hours</strong>.</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <h2 className="font-barrio text-4xl md:text-5xl text-[#2A382A] mb-8">
        Book your Adventure
      </h2>

      <div className="space-y-4">
        {availableDates.map((date, index) => (
          <div
            key={index}
            className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow"
          >
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-[#2A382A]/5 rounded-lg">
                  <CalendarRange className="w-6 h-6 text-[#2A382A]" />
                </div>
                <div>
                  <p className="font-medium text-[#2A382A]">{formatDate(date, {
                    weekday: 'long',
                    month: 'long',
                    day: 'numeric'
                  })}</p>
                  <p className="text-sm text-[#2A382A]/60">
                    This tour starts at {formatTime(tour.departure).americanTime} ({formatTime(tour.departure).europeanTime})
                    <br />
                    Meeting point: <a 
                      href={tour.meeting_point_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#953B0E] hover:underline"
                    >
                      {tour.meeting_point}
                    </a>
                  </p>
                </div>
              </div>

              <div className="flex flex-row items-center justify-between md:justify-end gap-4 w-full md:w-auto">
                <div className="flex flex-col items-start md:items-end">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-[#2A382A]/60">Starting at</span>
                    <span className="text-xl font-bold text-[#2A382A]">
                      ${Math.min(
                        tour.group_tour_price,
                        tour.reduced_group_price || Number.MAX_VALUE,
                        tour.private_price || Number.MAX_VALUE
                      )}
                    </span>
                  </div>
                  <p className="text-xs text-[#2A382A]/60">per person</p>
                </div>

                <Link 
                  to={`/tours/${tour.slug}/book?date=${date.toISOString()}`}
                  className="px-6 py-3 bg-[#953B0E] text-white rounded-lg hover:bg-[#ff6a0d] transition-colors whitespace-nowrap border-2 border-[#E85C0D]"
                >
                  Book Now
                </Link>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="flex justify-end mt-6">
        <Link
          to={`/tours/${tour.slug}/book`}
          className="flex items-center justify-between px-4 py-2 bg-[#953B0E] text-white rounded-[1rem] hover:bg-[#ff6a0d] transition-all duration-300 hover:scale-105 border-2 border-[#E85C0D]"
        >
          Book this tour for a later date
          <span className="bg-white/20 p-2 rounded-lg ml-6">
            <CalendarRange className="w-5 h-5" />
          </span>
        </Link>
      </div>
    </div>
  );
};

export default TourDetailBooking;